import {
    type ReactNode,
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    View
} from "react-native";
import {
    usePreviewText
} from "./previewText";
import {
    NCoreUIKitLocalize,
    type BadgeVariant,
    type IButtonProps,
    NCoreUIKitTheme,
    type BadgeType,
    DateSelector,
    RadioButton,
    ToggleGroup,
    AvatarGroup,
    RadialChart,
    RadioGroup,
    SelectBox,
    TextInput,
    Separator,
    AreaChart,
    StatTile,
    CheckBox,
    Progress,
    BarChart,
    PieChart,
    Avatar,
    Button,
    Switch,
    Slider,
    Badge,
    Alert,
    Empty,
    Field,
    Table,
    Chart,
    Card,
    Tabs,
    Text
} from "ncore-ui-kit";
import {
    TriangleAlert,
    CircleCheck,
    CircleAlert,
    Underline,
    UserPlus,
    Download,
    Italic,
    Trash2,
    Bold,
    Bell,
    Info
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

type WallCard = {
    render: () => ReactNode;
    weight: number;
    id: string;
};

type ButtonSample = {
    variant: NonNullable<IButtonProps["variant"]>;
    type: NonNullable<IButtonProps["type"]>;
    icon?: IButtonProps["icon"];
    isDisabled?: boolean;
    isLoading?: boolean;
    title: string;
};

type OrderStatus = "refunded" | "pending" | "paid";

const TriangleAlertIcon = createKitIcon(TriangleAlert);

const CircleCheckIcon = createKitIcon(CircleCheck);

const CircleAlertIcon = createKitIcon(CircleAlert);

const UnderlineIcon = createKitIcon(Underline);

const UserPlusIcon = createKitIcon(UserPlus);

const DownloadIcon = createKitIcon(Download);

const ItalicIcon = createKitIcon(Italic);

const TrashIcon = createKitIcon(Trash2);

const BoldIcon = createKitIcon(Bold);

const BellIcon = createKitIcon(Bell);

const InfoIcon = createKitIcon(Info);

const MAX_COLUMNS = 4;

const STAT_COLUMNS = 4;

const LAST_MONTH = new Date(2026, 9, 1);

const BADGE_TYPES: Array<BadgeType> = [
    "primary",
    "neutral",
    "success",
    "warning",
    "danger",
    "info"
];

const BADGE_VARIANTS: Array<BadgeVariant> = [
    "tonal",
    "solid",
    "outline"
];

const BADGE_ICONS: Partial<Record<BadgeType, IButtonProps["icon"]>> = {
    warning: TriangleAlertIcon,
    success: CircleCheckIcon,
    danger: CircleAlertIcon,
    info: InfoIcon
};

const STATUS_TYPES = [
    "info",
    "success",
    "warning",
    "danger"
] as const;

const BUTTON_ROWS: Array<Array<ButtonSample>> = [
    [
        {
            title: "createPreview.actions.publish",
            variant: "filled",
            type: "primary"
        },
        {
            title: "createPreview.actions.saveDraft",
            variant: "tonal",
            type: "primary"
        },
        {
            title: "createPreview.actions.preview",
            variant: "outline",
            type: "primary"
        },
        {
            title: "createPreview.actions.undo",
            variant: "ghost",
            type: "primary"
        },
        {
            title: "createPreview.actions.history",
            variant: "link",
            type: "primary"
        }
    ],
    [
        {
            title: "createPreview.actions.share",
            variant: "filled",
            type: "neutral"
        },
        {
            title: "createPreview.actions.duplicate",
            variant: "tonal",
            type: "neutral"
        },
        {
            title: "createPreview.actions.download",
            icon: DownloadIcon,
            variant: "outline",
            type: "neutral"
        },
        {
            title: "createPreview.actions.more",
            variant: "ghost",
            type: "neutral"
        }
    ],
    [
        {
            title: "createPreview.actions.delete",
            variant: "filled",
            icon: TrashIcon,
            type: "danger"
        },
        {
            title: "createPreview.actions.removeAccess",
            variant: "tonal",
            type: "danger"
        },
        {
            title: "createPreview.actions.leave",
            variant: "outline",
            type: "danger"
        }
    ],
    [
        {
            title: "createPreview.actions.saving",
            variant: "filled",
            type: "primary",
            isLoading: true
        },
        {
            title: "createPreview.actions.locked",
            variant: "outline",
            isDisabled: true,
            type: "neutral"
        }
    ]
];

const ROLES = [
    "roleDesigner",
    "roleEngineer",
    "roleProduct"
];

const MEMBERS = [
    {
        role: "createPreview.team.owner",
        email: "elif@nibgat.com",
        name: "Elif Demir"
    },
    {
        role: "createPreview.team.editor",
        email: "can@nibgat.com",
        name: "Can Yılmaz"
    },
    {
        role: "createPreview.team.viewer",
        email: "zeynep@nibgat.com",
        name: "Zeynep Kaya"
    }
];

const TEAM = [
    "Elif Demir",
    "Can Yılmaz",
    "Zeynep Kaya",
    "Mert Aydın",
    "Deniz Şahin",
    "Ece Arslan",
    "Burak Çelik"
];

const ORDERS: Array<{
    status: OrderStatus;
    customer: string;
    amount: number;
    id: string;
}> = [
    {
        customer: "Elif Demir",
        status: "paid",
        amount: 1250,
        id: "order1"
    },
    {
        customer: "Can Yılmaz",
        status: "pending",
        amount: 480,
        id: "order2"
    },
    {
        customer: "Zeynep Kaya",
        status: "paid",
        amount: 2310,
        id: "order3"
    },
    {
        customer: "Mert Aydın",
        status: "refunded",
        id: "order4",
        amount: 95
    },
    {
        customer: "Deniz Şahin",
        status: "paid",
        amount: 760,
        id: "order5"
    }
];

const ORDER_BADGES: Record<OrderStatus, BadgeType> = {
    refunded: "neutral",
    pending: "warning",
    paid: "success"
};

const VISITORS = {
    desktop: [
        1840,
        2120,
        1980,
        2460,
        2710,
        3020
    ],
    mobile: [
        1260,
        1410,
        1530,
        1720,
        1960,
        2240
    ]
};

const CASHFLOW = {
    revenue: [
        42000,
        48000,
        45000,
        53000,
        58000,
        61000
    ],
    spend: [
        31000,
        33000,
        36000,
        34000,
        39000,
        41000
    ]
};

const TRAFFIC = [
    {
        source: "search",
        visits: 4820
    },
    {
        source: "direct",
        visits: 2960
    },
    {
        source: "social",
        visits: 1730
    },
    {
        source: "email",
        visits: 940
    }
];

const STATS = [
    {
        title: "createPreview.stats.revenue",
        kind: "currency",
        id: "revenue",
        value: 284120,
        delta: 0.124,
        trend: [
            212,
            236,
            228,
            251,
            267,
            284
        ]
    },
    {
        title: "createPreview.stats.customers",
        id: "customers",
        kind: "integer",
        delta: 0.082,
        value: 1284,
        trend: [
            980,
            1040,
            1110,
            1090,
            1186,
            1284
        ]
    },
    {
        title: "createPreview.stats.subscriptions",
        id: "subscriptions",
        kind: "integer",
        delta: -0.031,
        value: 9412,
        trend: [
            9890,
            9820,
            9760,
            9690,
            9710,
            9412
        ]
    },
    {
        title: "createPreview.stats.conversion",
        id: "conversion",
        kind: "percent",
        delta: 0.006,
        value: 0.038,
        trend: [
            3.1,
            3.3,
            3.2,
            3.5,
            3.6,
            3.8
        ]
    }
];

const stylesheet = StyleSheet.create({
    teamRow: {
        justifyContent: "space-between",
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap"
    },
    memberText: {
        flexDirection: "column",
        minWidth: 0,
        flex: 1,
        flexShrink: 1
    },
    column: {
        flexDirection: "column",
        minWidth: 0,
        flex: 1
    },
    stack: {
        flexDirection: "column"
    },
    memberRow: {
        flexDirection: "row",
        alignItems: "center"
    },
    wrapRow: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap"
    },
    row: {
        flexDirection: "row"
    },
    tile: {
        minWidth: 0,
        flex: 1
    }
});

const handlePress = () => undefined;

const getOptionTitle = (option: {
    name: string;
}) => option.name;

const getOptionKey = (option: {
    id: string;
}) => option.id;

const PreviewWall = () => {
    const t = usePreviewText();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        isUpdatesOn,
        setIsUpdatesOn
    ] = useState(true);

    const [
        isDigestOn,
        setIsDigestOn
    ] = useState(true);

    const [
        isPushOn,
        setIsPushOn
    ] = useState(false);

    const [
        meetingDate,
        setMeetingDate
    ] = useState<Date | undefined>(() => new Date());

    const currencyCode = t("createPreview.currency");

    const currency = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 0,
        currency: currencyCode,
        style: "currency"
    });

    const compactCurrency = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 0,
        currency: currencyCode,
        notation: "compact",
        style: "currency"
    });

    const percent = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
        style: "percent"
    });

    const integer = new Intl.NumberFormat(activeLocale);

    const monthFormat = new Intl.DateTimeFormat(activeLocale, {
        month: "short"
    });

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const pagePadding = isCompact ? spaces.spacingMd : spaces.spacingLg;

    const available = width - pagePadding * 2;

    const columnCount = isCompact ? 1 : Math.max(1, Math.min(MAX_COLUMNS, Math.round((available + spaces.spacingMd) / (shapeConfigs.cardWidth + spaces.spacingMd))));

    const statColumns = columnCount >= 3 ? STAT_COLUMNS : columnCount === 2 ? 2 : 1;

    const months = VISITORS.desktop.map((_, index) => new Date(LAST_MONTH.getFullYear(), LAST_MONTH.getMonth() - (VISITORS.desktop.length - 1 - index), 1).toISOString());

    const visitorRows = months.map((month, index) => ({
        desktop: VISITORS.desktop[index] ?? 0,
        mobile: VISITORS.mobile[index] ?? 0,
        month
    }));

    const cashflowRows = months.map((month, index) => ({
        revenue: CASHFLOW.revenue[index] ?? 0,
        spend: CASHFLOW.spend[index] ?? 0,
        month
    }));

    const lastVisitors = (VISITORS.desktop[5] ?? 0) + (VISITORS.mobile[5] ?? 0);

    const previousVisitors = (VISITORS.desktop[4] ?? 0) + (VISITORS.mobile[4] ?? 0);

    const roleOptions = ROLES.map((role) => ({
        name: t(`createPreview.account.${role}`),
        id: role
    }));

    const formatMonth = (value: string | number) => monthFormat.format(new Date(String(value)));

    const formatStat = (kind: string, value: number) => {
        if(kind === "currency") {
            return currency.format(value);
        }

        if(kind === "percent") {
            return percent.format(value);
        }

        return integer.format(value);
    };

    const renderCardHeader = (key: string, action?: ReactNode) => <Card.Header>
        <Card.Title>
            {t(`createPreview.${key}.title`)}
        </Card.Title>
        <Card.Description>
            {t(`createPreview.${key}.description`)}
        </Card.Description>
        {
            action ?
                <Card.Action>
                    {action}
                </Card.Action>
            :
                null
        }
    </Card.Header>;

    const renderAccount = () => <Card>
        {renderCardHeader("account")}
        <Card.Content>
            <Field.Group>
                <TextInput
                    defaultValue={t("createPreview.account.nameValue")}
                    title={t("createPreview.account.name")}
                    spreadBehaviour="stretch"
                    autoComplete="name"
                />
                <TextInput
                    placeholder={t("createPreview.account.emailPlaceholder")}
                    title={t("createPreview.account.email")}
                    keyboardType="email-address"
                    spreadBehaviour="stretch"
                    autoComplete="email"
                />
                <SelectBox
                    title={t("createPreview.account.role")}
                    titleExtractor={getOptionTitle}
                    defaultValue="roleDesigner"
                    keyExtractor={getOptionKey}
                    spreadBehaviour="stretch"
                    data={roleOptions}
                />
                <CheckBox
                    title={t("createPreview.account.updates")}
                    onCheckedChange={setIsUpdatesOn}
                    isChecked={isUpdatesOn}
                />
            </Field.Group>
        </Card.Content>
        <Card.Footer
            variant="band"
            align="end"
        >
            <Button
                title={t("createPreview.account.cancel")}
                onPress={handlePress}
                variant="outline"
                type="neutral"
            />
            <Button
                title={t("createPreview.account.submit")}
                onPress={handlePress}
            />
        </Card.Footer>
    </Card>;

    const renderNotifications = () => <Card>
        {renderCardHeader("notifications")}
        <Card.Content>
            <Field.Set>
                <Field>
                    <Field.Title>
                        {t("createPreview.notifications.legend")}
                    </Field.Title>
                    <RadioGroup
                        defaultValue="mentions"
                    >
                        <RadioButton
                            subTitle={t("createPreview.notifications.allDescription")}
                            title={t("createPreview.notifications.all")}
                            value="all"
                        />
                        <RadioButton
                            subTitle={t("createPreview.notifications.mentionsDescription")}
                            title={t("createPreview.notifications.mentions")}
                            value="mentions"
                        />
                        <RadioButton
                            subTitle={t("createPreview.notifications.noneDescription")}
                            title={t("createPreview.notifications.none")}
                            value="none"
                        />
                    </RadioGroup>
                </Field>
                <Separator/>
                <Field
                    orientation="horizontal"
                >
                    <Field.Content>
                        <Field.Title>
                            {t("createPreview.notifications.digest")}
                        </Field.Title>
                        <Field.Description>
                            {t("createPreview.notifications.digestDescription")}
                        </Field.Description>
                    </Field.Content>
                    <Switch
                        onCheckedChange={setIsDigestOn}
                        isChecked={isDigestOn}
                    />
                </Field>
                <Field
                    orientation="horizontal"
                >
                    <Field.Content>
                        <Field.Title>
                            {t("createPreview.notifications.push")}
                        </Field.Title>
                        <Field.Description>
                            {t("createPreview.notifications.pushDescription")}
                        </Field.Description>
                    </Field.Content>
                    <Switch
                        onCheckedChange={setIsPushOn}
                        isChecked={isPushOn}
                    />
                </Field>
            </Field.Set>
        </Card.Content>
    </Card>;

    const renderPrice = () => <Card>
        {renderCardHeader("price")}
        <Card.Content>
            <Field.Group>
                <Slider
                    formatValueText={(value) => currency.format(value)}
                    title={t("createPreview.price.range")}
                    thumbTitles={[
                        t("createPreview.price.min"),
                        t("createPreview.price.max")
                    ]}
                    defaultValue={[
                        1200,
                        4800
                    ]}
                    max={10000}
                    step={100}
                    min={0}
                />
                <Slider
                    formatValueText={(value) => integer.format(value)}
                    title={t("createPreview.price.rating")}
                    isShowTicks={true}
                    defaultValue={4}
                    max={5}
                    min={1}
                />
            </Field.Group>
        </Card.Content>
        <Card.Footer
            variant="band"
            align="end"
        >
            <Button
                title={t("createPreview.price.reset")}
                onPress={handlePress}
                variant="ghost"
                type="neutral"
            />
            <Button
                title={t("createPreview.price.apply")}
                onPress={handlePress}
            />
        </Card.Footer>
    </Card>;

    const renderActions = () => <Card>
        {renderCardHeader("actions")}
        <Card.Content>
            <View
                style={[
                    stylesheet.stack,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                {
                    BUTTON_ROWS.map((row, rowIndex) => <View
                        style={[
                            stylesheet.wrapRow,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                        key={`row${rowIndex}`}
                    >
                        {
                            row.map((sample) => <Button
                                isDisabled={sample.isDisabled}
                                isLoading={sample.isLoading}
                                variant={sample.variant}
                                title={t(sample.title)}
                                onPress={handlePress}
                                icon={sample.icon}
                                type={sample.type}
                                key={sample.title}
                            />)
                        }
                    </View>)
                }
                <ToggleGroup
                    title={t("createPreview.report.format")}
                    selectBehaviour="multiple"
                    attachBehaviour="attached"
                    variant="outline"
                    defaultValue={[
                        "bold"
                    ]}
                >
                    <ToggleGroup.Item
                        accessibilityLabel={t("createPreview.actions.bold")}
                        isIconOnly={true}
                        icon={BoldIcon}
                        value="bold"
                    />
                    <ToggleGroup.Item
                        accessibilityLabel={t("createPreview.actions.italic")}
                        isIconOnly={true}
                        icon={ItalicIcon}
                        value="italic"
                    />
                    <ToggleGroup.Item
                        accessibilityLabel={t("createPreview.actions.underline")}
                        icon={UnderlineIcon}
                        isIconOnly={true}
                        value="underline"
                    />
                </ToggleGroup>
            </View>
        </Card.Content>
    </Card>;

    const renderLabels = () => <Card>
        {renderCardHeader("labels")}
        <Card.Content>
            <View
                style={[
                    stylesheet.stack,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                {
                    BADGE_VARIANTS.map((variant) => <View
                        style={[
                            stylesheet.wrapRow,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                        key={variant}
                    >
                        {
                            BADGE_TYPES.map((type) => <Badge
                                icon={variant === "tonal" ? BADGE_ICONS[type] : undefined}
                                title={t(`createPreview.labels.${type}`)}
                                variant={variant}
                                type={type}
                                key={type}
                            />)
                        }
                    </View>)
                }
            </View>
        </Card.Content>
    </Card>;

    const renderStatus = () => <Card>
        <Card.Header>
            <Card.Title>
                {t("createPreview.status.title")}
            </Card.Title>
        </Card.Header>
        <Card.Content>
            <View
                style={[
                    stylesheet.stack,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                {
                    STATUS_TYPES.map((type) => <Alert
                        description={t(`createPreview.status.${type}Description`)}
                        title={t(`createPreview.status.${type}`)}
                        type={type}
                        key={type}
                    />)
                }
            </View>
        </Card.Content>
    </Card>;

    const renderReport = () => <Card>
        {renderCardHeader("report")}
        <Card.Content>
            <View
                style={[
                    stylesheet.stack,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Tabs
                    spreadBehaviour="stretch"
                    defaultValue="overview"
                    variant="segment"
                >
                    <Tabs.List
                        title={t("createPreview.report.tabs")}
                    >
                        <Tabs.Trigger
                            title={t("createPreview.report.overview")}
                            value="overview"
                        />
                        <Tabs.Trigger
                            title={t("createPreview.report.analytics")}
                            value="analytics"
                        />
                        <Tabs.Trigger
                            title={t("createPreview.report.export")}
                            value="export"
                        />
                    </Tabs.List>
                    <Tabs.Content
                        value="overview"
                    >
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("createPreview.report.overviewText")}
                        </Text>
                    </Tabs.Content>
                    <Tabs.Content
                        value="analytics"
                    >
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("createPreview.report.analyticsText")}
                        </Text>
                    </Tabs.Content>
                    <Tabs.Content
                        value="export"
                    >
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("createPreview.report.exportText")}
                        </Text>
                    </Tabs.Content>
                </Tabs>
                <ToggleGroup
                    title={t("createPreview.report.range")}
                    attachBehaviour="attached"
                    defaultValue="week"
                    variant="outline"
                >
                    <ToggleGroup.Item
                        title={t("createPreview.report.day")}
                        value="day"
                    />
                    <ToggleGroup.Item
                        title={t("createPreview.report.week")}
                        value="week"
                    />
                    <ToggleGroup.Item
                        title={t("createPreview.report.month")}
                        value="month"
                    />
                </ToggleGroup>
            </View>
        </Card.Content>
    </Card>;

    const renderTeam = () => <Card>
        {
            renderCardHeader("team", <Button
                title={t("createPreview.team.invite")}
                onPress={handlePress}
                icon={UserPlusIcon}
                variant="outline"
                type="neutral"
                size="small"
            />)
        }
        <Card.Content>
            <View
                style={[
                    stylesheet.stack,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <AvatarGroup
                    accessibilityLabel={t("createPreview.team.group")}
                    avatars={TEAM.map((name) => ({
                        title: name
                    }))}
                    size="small"
                    max={5}
                />
                <Separator/>
                {
                    MEMBERS.map((member) => <View
                        style={[
                            stylesheet.memberRow,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                        key={member.email}
                    >
                        <Avatar
                            title={member.name}
                            size="small"
                        />
                        <View
                            style={stylesheet.memberText}
                        >
                            <Text
                                variant="bodyMediumSize"
                                numberOfLines={1}
                                color="high"
                            >
                                {member.name}
                            </Text>
                            <Text
                                variant="bodySmallSize"
                                numberOfLines={1}
                                color="low"
                            >
                                {member.email}
                            </Text>
                        </View>
                        <Badge
                            title={t(member.role)}
                            variant="outline"
                            type="neutral"
                        />
                    </View>)
                }
            </View>
        </Card.Content>
    </Card>;

    const renderUploads = () => <Card>
        {renderCardHeader("uploads")}
        <Card.Content>
            <View
                style={[
                    stylesheet.stack,
                    {
                        gap: spaces.spacingLg
                    }
                ]}
            >
                <Progress
                    description={t("createPreview.uploads.fileSize")}
                    title={t("createPreview.uploads.file")}
                    value={64}
                />
                <Progress
                    description={t("createPreview.uploads.setupDescription")}
                    title={t("createPreview.uploads.setup")}
                    variant="segmented"
                    steps={5}
                    value={3}
                />
                <Progress
                    valueFormatter={() => t("createPreview.uploads.storageFull")}
                    title={t("createPreview.uploads.storage")}
                    isMeter={true}
                    type="warning"
                    value={92}
                />
                <View
                    style={[
                        stylesheet.memberRow,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Progress
                        title={t("createPreview.uploads.import")}
                        variant="circular"
                        value={64}
                    />
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {t("createPreview.uploads.import")}
                    </Text>
                </View>
            </View>
        </Card.Content>
    </Card>;

    const renderOrders = () => <Card>
        {
            renderCardHeader("orders", <Button
                title={t("createPreview.orders.viewAll")}
                onPress={handlePress}
                variant="link"
                size="small"
            />)
        }
        <Card.Content
            isFlush={true}
        >
            <Table
                caption={t("createPreview.orders.title")}
                isCaptionHidden={true}
                size="small"
            >
                <Table.Header>
                    <Table.Row>
                        <Table.Head>
                            {t("createPreview.orders.customer")}
                        </Table.Head>
                        <Table.Head
                            isShrink={true}
                        >
                            {t("createPreview.orders.status")}
                        </Table.Head>
                        <Table.Head
                            isNumeric={true}
                            isShrink={true}
                        >
                            {t("createPreview.orders.amount")}
                        </Table.Head>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {
                        ORDERS.map((order) => <Table.Row
                            key={order.id}
                        >
                            <Table.Cell>
                                {order.customer}
                            </Table.Cell>
                            <Table.Cell
                                isShrink={true}
                            >
                                <Badge
                                    title={t(`createPreview.orders.${order.status}`)}
                                    type={ORDER_BADGES[order.status]}
                                />
                            </Table.Cell>
                            <Table.Cell
                                isNumeric={true}
                                isShrink={true}
                            >
                                {currency.format(order.amount)}
                            </Table.Cell>
                        </Table.Row>)
                    }
                </Table.Body>
            </Table>
        </Card.Content>
    </Card>;

    const renderVisitors = () => <Chart
        description={t("createPreview.visitors.description")}
        config={{
            desktop: {
                title: t("createPreview.visitors.desktop"),
                colorIndex: 1
            },
            mobile: {
                title: t("createPreview.visitors.mobile"),
                colorIndex: 2
            }
        }}
        title={t("createPreview.visitors.title")}
        spreadBehaviour="stretch"
    >
        <Chart.Value
            delta={(lastVisitors - previousVisitors) / previousVisitors}
            description={t("createPreview.visitors.vsLastMonth")}
            value={integer.format(lastVisitors)}
        />
        <AreaChart
            categoryFormatter={formatMonth}
            stackBehaviour="stacked"
            categoryKey="month"
            data={visitorRows}
            plotSize="small"
            fill="gradient"
            curve="natural"
        />
    </Chart>;

    const renderCashflow = () => <Chart
        description={t("createPreview.cashflow.description")}
        config={{
            revenue: {
                title: t("createPreview.cashflow.revenue"),
                colorIndex: 1
            },
            spend: {
                title: t("createPreview.cashflow.spend"),
                colorIndex: 2
            }
        }}
        title={t("createPreview.cashflow.title")}
        spreadBehaviour="stretch"
    >
        <BarChart
            valueFormatter={(value) => compactCurrency.format(value)}
            categoryTitle={t("createPreview.cashflow.month")}
            categoryFormatter={formatMonth}
            categoryKey="month"
            data={cashflowRows}
            plotSize="small"
        />
    </Chart>;

    const renderTraffic = () => <Chart
        description={t("createPreview.traffic.description")}
        config={{
            direct: {
                title: t("createPreview.traffic.direct"),
                colorIndex: 2
            },
            search: {
                title: t("createPreview.traffic.search"),
                colorIndex: 1
            },
            social: {
                title: t("createPreview.traffic.social"),
                colorIndex: 3
            },
            email: {
                title: t("createPreview.traffic.email"),
                colorIndex: 4
            }
        }}
        title={t("createPreview.traffic.title")}
        spreadBehaviour="stretch"
    >
        <PieChart
            centerValue={integer.format(TRAFFIC.reduce((total, row) => total + row.visits, 0))}
            categoryTitle={t("createPreview.traffic.source")}
            valueFormatter={(value) => integer.format(value)}
            centerTitle={t("createPreview.traffic.total")}
            categoryKey="source"
            valueKey="visits"
            plotSize="small"
            variant="donut"
            data={TRAFFIC}
        />
    </Chart>;

    const renderStorage = () => <Chart
        description={t("createPreview.storage.description")}
        config={{
            used: {
                title: t("createPreview.storage.used"),
                colorIndex: 1
            }
        }}
        title={t("createPreview.storage.title")}
        spreadBehaviour="stretch"
    >
        <RadialChart
            centerTitle={t("createPreview.storage.used")}
            variant="progress"
            plotSize="small"
            maxValue={1000}
            valueKey="used"
            value={720}
        />
    </Chart>;

    const renderMeeting = () => <Card>
        {renderCardHeader("meeting")}
        <Card.Content>
            <DateSelector
                selectMultipleObject={() => undefined}
                selectObject={setMeetingDate}
                isShowCard={false}
                date={meetingDate}
                variant="single"
            />
        </Card.Content>
        <Card.Footer
            variant="band"
            align="end"
        >
            <Button
                title={t("createPreview.meeting.confirm")}
                onPress={handlePress}
            />
        </Card.Footer>
    </Card>;

    const renderDialog = () => <Card
        variant="elevated"
    >
        {renderCardHeader("dialog")}
        <Card.Content>
            <TextInput
                placeholder={t("createPreview.dialog.confirmPlaceholder")}
                title={t("createPreview.dialog.confirmTitle")}
                spreadBehaviour="stretch"
            />
        </Card.Content>
        <Card.Footer
            variant="band"
            align="end"
        >
            <Button
                title={t("createPreview.dialog.cancel")}
                onPress={handlePress}
                variant="outline"
                type="neutral"
            />
            <Button
                title={t("createPreview.dialog.delete")}
                onPress={handlePress}
                variant="filled"
                type="danger"
            />
        </Card.Footer>
    </Card>;

    const renderEmpty = () => <Empty
        variant="outline"
    >
        <Empty.Header>
            <Empty.Media
                icon={BellIcon}
            />
            <Empty.Title>
                {t("createPreview.empty.title")}
            </Empty.Title>
            <Empty.Description>
                {t("createPreview.empty.description")}
            </Empty.Description>
        </Empty.Header>
        <Empty.Content>
            <Button
                title={t("createPreview.empty.action")}
                onPress={handlePress}
                variant="outline"
                type="neutral"
                size="small"
            />
        </Empty.Content>
    </Empty>;

    const cards: Array<WallCard> = [
        {
            render: renderAccount,
            id: "account",
            weight: 460
        },
        {
            render: renderVisitors,
            id: "visitors",
            weight: 400
        },
        {
            render: renderNotifications,
            id: "notifications",
            weight: 440
        },
        {
            render: renderActions,
            id: "actions",
            weight: 330
        },
        {
            render: renderOrders,
            id: "orders",
            weight: 380
        },
        {
            render: renderLabels,
            id: "labels",
            weight: 200
        },
        {
            render: renderCashflow,
            id: "cashflow",
            weight: 380
        },
        {
            render: renderTeam,
            id: "team",
            weight: 340
        },
        {
            render: renderStatus,
            id: "status",
            weight: 440
        },
        {
            render: renderPrice,
            id: "price",
            weight: 300
        },
        {
            render: renderTraffic,
            id: "traffic",
            weight: 420
        },
        {
            render: renderReport,
            id: "report",
            weight: 300
        },
        {
            render: renderUploads,
            id: "uploads",
            weight: 380
        },
        {
            render: renderMeeting,
            id: "meeting",
            weight: 480
        },
        {
            render: renderStorage,
            id: "storage",
            weight: 360
        },
        {
            render: renderDialog,
            id: "dialog",
            weight: 300
        },
        {
            render: renderEmpty,
            id: "empty",
            weight: 280
        }
    ];

    const columns = cards.reduce<Array<{
        cards: Array<WallCard>;
        height: number;
    }>>((result, card) => {
        const shortest = result.reduce((best, column, index) => column.height < (result[best]?.height ?? 0) ? index : best, 0);

        const target = result[shortest];

        if(target) {
            target.cards.push(card);

            target.height += card.weight;
        }

        return result;
    }, Array.from({
        length: columnCount
    }, () => ({
        cards: [],
        height: 0
    })));

    const statRows = Array.from({
        length: Math.ceil(STATS.length / statColumns)
    }, (_, index) => STATS.slice(index * statColumns, (index + 1) * statColumns));

    return <View
        style={[
            stylesheet.stack,
            {
                padding: pagePadding,
                gap: spaces.spacingMd
            }
        ]}
    >
        {
            statRows.map((statRow, rowIndex) => <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
                key={`stats${rowIndex}`}
            >
                {
                    statRow.map((stat) => <StatTile
                        style={stylesheet.tile}
                        isTrendFlush={true}
                        trend={stat.trend}
                        key={stat.id}
                    >
                        <StatTile.Title>
                            {t(stat.title)}
                        </StatTile.Title>
                        <StatTile.Value>
                            {formatStat(stat.kind, stat.value)}
                        </StatTile.Value>
                        <StatTile.Delta
                            description={t("createPreview.stats.vsLastMonth")}
                            value={stat.delta}
                        />
                    </StatTile>)
                }
            </View>)
        }
        <View
            style={[
                stylesheet.row,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            {
                columns.map((column, columnIndex) => <View
                    style={[
                        stylesheet.column,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                    key={`column${columnIndex}`}
                >
                    {
                        column.cards.map((card) => <View
                            key={card.id}
                        >
                            {card.render()}
                        </View>)
                    }
                </View>)
            }
        </View>
    </View>;
};
export default PreviewWall;
